import { API_BASE_URL } from "../config/payments";
import { auth, firestore } from "../firebase";

export const REFERRAL_CODE_PATTERN = /^[A-Z]{2}\d{4,6}$/;

export function normalizeIndianPhoneNumber(phoneNumber) {
  const digits = String(phoneNumber || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.startsWith("91") && digits.length === 12) return digits;
  return digits;
}

export function formatVerifiedPhone(phoneNumber) {
  const digits = normalizeIndianPhoneNumber(phoneNumber);
  return digits ? `+${digits}` : "";
}

function createApiError(message, code, status) {
  const error = new Error(message);
  error.code = code;
  error.status = status;
  return error;
}

async function parseJson(response) {
  return response.json().catch(() => ({}));
}

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) throw createApiError("Not signed in", "auth-required", 401);
  const idToken = await user.getIdToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${idToken}`,
  };
}

export async function sendOtp(phoneNumber) {
  const formattedPhone = normalizeIndianPhoneNumber(phoneNumber);
  const response = await fetch(`${API_BASE_URL}/auth/otp/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phoneNumber: formattedPhone }),
  });
  const json = await parseJson(response);

  if (!response.ok || !json?.success || !json?.challengeId) {
    throw createApiError(json?.error || "Failed to send OTP", json?.code, response.status);
  }

  return {
    challengeId: json.challengeId,
    phoneNumber: formatVerifiedPhone(formattedPhone),
    testOtp: Boolean(json.testOtp),
  };
}

export async function fetchCurrentUserProfile(uid) {
  if (!uid) return null;
  const snap = await firestore.collection("users").doc(uid).get();
  return snap.exists ? { uid, ...snap.data() } : null;
}

export async function verifyOtp(challenge, otp) {
  const response = await fetch(`${API_BASE_URL}/auth/otp/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      challengeId: challenge?.challengeId,
      phoneNumber: String(challenge?.phoneNumber || "").replace(/\D/g, ""),
      otp,
    }),
  });
  const json = await parseJson(response);

  if (!response.ok || !json?.success || !json?.customToken) {
    throw createApiError(json?.error || "Failed to verify OTP", json?.code, response.status);
  }

  const credential = await auth.signInWithCustomToken(json.customToken);
  const firebaseUser = credential.user;
  const user = await fetchCurrentUserProfile(firebaseUser.uid);

  return {
    firebaseUser,
    user,
    uid: json.uid || firebaseUser.uid,
    phoneNumber: json.phoneNumber || challenge?.phoneNumber || "",
  };
}

export async function validateReferralCode(referralCode) {
  const normalized = String(referralCode || "").trim().toUpperCase();
  if (!normalized) return { valid: true };
  if (!REFERRAL_CODE_PATTERN.test(normalized)) {
    return { valid: false, reason: "invalid_format" };
  }

  const response = await fetch(`${API_BASE_URL}/auth/referral/validate`, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify({ referralCode: normalized }),
  });
  const json = await parseJson(response);

  if (response.ok && json?.success) {
    return json.data || { valid: false };
  }

  if (response.status === 400 && json?.code === "invalid-referral-code") {
    return { valid: false, reason: "invalid_format" };
  }

  throw createApiError(json?.error || "Failed to validate referral code", json?.code, response.status);
}

export async function completeRegistration(payload) {
  const response = await fetch(`${API_BASE_URL}/auth/register/complete`, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  const json = await parseJson(response);

  if (!response.ok || !json?.success || !json?.data?.user) {
    throw createApiError(json?.error || "Failed to complete registration", json?.code, response.status);
  }

  return json.data.user;
}

export function getDisplayName(user) {
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
  return fullName || user?.displayName || "Got Tea Customer";
}
